/**
 * Claims HTTP Repository - /api/v1/claims
 */

import { httpClient } from '@shared/infrastructure/http/base.client';
import type {
  ClaimResponse,
  CreateClaimRequest,
  CreateClaimResponse,
  ReviewClaimRequest,
  PayClaimRequest,
  UploadClaimFileResponse,
} from '../../../application/dto/ClaimDTO';

const BASE = '/claims';

/** El backend pagina la lista: { claims, limit, page, total }. */
interface ClaimsListResponse {
  claims: ClaimResponse[];
  limit?: number;
  page?: number;
  total?: number;
}

export const claimsApi = {
  async listAll(): Promise<ClaimResponse[]> {
    const raw = await httpClient.get<ClaimsListResponse>(`${BASE}/admin/list`);
    return raw?.claims ?? [];
  },
  async list(): Promise<ClaimResponse[]> {
    const raw = await httpClient.get<ClaimResponse[] | ClaimsListResponse>(BASE);
    if (Array.isArray(raw)) return raw;
    return raw?.claims ?? [];
  },

  get(id: string): Promise<ClaimResponse> {
    return httpClient.get<ClaimResponse>(`${BASE}/${id}`);
  },

  create(body: CreateClaimRequest): Promise<CreateClaimResponse> {
    return httpClient.post<CreateClaimResponse>(BASE, body);
  },

  /** Subir el formulario PDF de solicitud. Multipart `file`. */
  uploadRequestForm(claimId: string, file: File): Promise<UploadClaimFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.post<UploadClaimFileResponse>(
      `${BASE}/${claimId}/request-form`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /** Subir una boleta/factura. Multipart `file`. */
  uploadInvoice(claimId: string, file: File): Promise<UploadClaimFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.post<UploadClaimFileResponse>(
      `${BASE}/${claimId}/invoices`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  review(id: string, body: ReviewClaimRequest): Promise<ClaimResponse> {
    return httpClient.post<ClaimResponse>(`${BASE}/${id}/review`, body);
  },

  pay(id: string, body: PayClaimRequest): Promise<ClaimResponse> {
    return httpClient.post<ClaimResponse>(`${BASE}/${id}/pay`, body);
  },
};
